// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — Compare's A − B drawn every frame on the graphics card (research
 * tier, 2026-10-06), as the change from a baseline already is.
 *
 * The paused picture (`compareFields`) averages each field onto a common
 * grid as coarse as the coarser one, then subtracts. The live picture does
 * the same sums on the card: for the cell a pixel falls in, it reads the
 * very texels the paused picture averages (the column and row tables of
 * `cellIndex`, uploaded once), turns each code into its value through the
 * same tables, averages where at least half carry data, converts B into A's
 * units, subtracts (or divides, as a %), and picks the colour from the same
 * 1024-step palette. Two frames are sampled per pixel: A's own texture and
 * B's frame, which is copied to A's card every frame.
 *
 * {@link mirrorCell} repeats the shader's arithmetic in float32, so a test
 * can hold the two pictures to the same colours. Pure.
 */
import type { LumaSnapshot } from '../glLumaSampler'
import { buildChangePalette, CHANGE_GREY, DEFAULT_CHANGE_COLOURS, PALETTE_STEPS, valueTable, type ChangeColours, type ChangeRange } from './changeMap'
import { cellIndex, type Affine, type CompareField } from './compare'

/** Width of the index texture: the column and row tables are packed into rows of this many texels. */
export const INDEX_TEXTURE_WIDTH = 4096
/** The shader's loop bound: at most this many sub-samples per cell and direction (`compare.ts` MAX_SUBSAMPLES). */
export const MAX_LIVE_SUBSAMPLES = 8

/** Everything the dataset shader needs to draw A − B, apart from B's frame itself. */
export interface LiveCompareTables {
  /** The common grid, cells. */
  readonly width: number
  readonly height: number
  /** Sub-samples per cell and direction, A and B. */
  readonly ka: number
  readonly kb: number
  /** Columns of A, rows of A, columns of B, rows of B, packed; −1 outside the field. */
  readonly index: Float32Array
  /** Where each of the four tables starts in `index`. */
  readonly offsets: readonly [number, number, number, number]
  /** 256 × 2 × (value, 1 for data): row 0 A's codes, row 1 B's in B's own units. */
  readonly values: Float32Array
  /** B in A's units: factor, offset. */
  readonly convert: readonly [number, number]
  /** RGBA, 1024 steps (`buildChangePalette`). */
  readonly palette: Uint8Array
  /** 0 difference, 1 % of B. */
  readonly mode: 0 | 1
  /** The + end of the range. */
  readonly limit: number
  /** AYNI (2026-10-20): the − end's size (the viewer's range); `limit` when not given. */
  readonly lower?: number
  /** % mode: no colour where |B| is under this. */
  readonly floor: number
  /** 0..1 RGBA. */
  readonly grey: readonly [number, number, number, number]
}

/**
 * The tables for a pair, from the frames the paused picture was computed on
 * (their grids, not their codes: the card reads codes from the frames it
 * draws). `grid` is the paused picture's, so both use one grid, range and
 * floor.
 */
export function buildLiveCompare(
  a: CompareField,
  b: CompareField,
  convert: Affine,
  grid: { width: number; height: number; mode: 'difference' | 'percent' | 'sigma'; limit: number; range?: ChangeRange; floor: number | null },
  /** AYNI (2026-10-20): the change map's palette. */
  colours: ChangeColours = DEFAULT_CHANGE_COLOURS,
): LiveCompareTables | null {
  if (grid.mode === 'sigma') return null
  const ia = cellIndex(a, grid.width, grid.height)
  const ib = cellIndex(b, grid.width, grid.height)
  if (ia.k > MAX_LIVE_SUBSAMPLES || ib.k > MAX_LIVE_SUBSAMPLES) return null
  const parts = [ia.cols, ia.rows, ib.cols, ib.rows]
  const total = parts.reduce((n, p) => n + p.length, 0)
  const index = new Float32Array(Math.ceil(total / INDEX_TEXTURE_WIDTH) * INDEX_TEXTURE_WIDTH).fill(-1)
  const offsets: number[] = []
  let at = 0
  for (const p of parts) {
    offsets.push(at)
    index.set(p, at)
    at += p.length
  }
  const values = new Float32Array(256 * 2 * 2)
  for (const [row, table] of [[0, valueTable(a.scale)], [1, valueTable(b.scale)]] as const) {
    for (let c = 0; c < 256; c++) {
      const ok = Number.isFinite(table[c])
      values[(row * 256 + c) * 2] = ok ? table[c] : 0
      values[(row * 256 + c) * 2 + 1] = ok ? 1 : 0
    }
  }
  return {
    width: grid.width,
    height: grid.height,
    ka: ia.k,
    kb: ib.k,
    index,
    offsets: offsets as unknown as [number, number, number, number],
    values,
    convert: [convert.factor, convert.offset],
    palette: buildChangePalette(PALETTE_STEPS, colours),
    mode: grid.mode === 'percent' ? 1 : 0,
    limit: grid.range?.max ?? grid.limit,
    lower: grid.range ? -grid.range.min : grid.limit,
    floor: grid.floor ?? 0,
    grey: [CHANGE_GREY[0] / 255, CHANGE_GREY[1] / 255, CHANGE_GREY[2] / 255, 1],
  }
}

const f = Math.fround

/** One side's mean in a cell as the shader takes it (float32); NaN where under half the sub-samples carry data. */
function mirrorMean(t: LiveCompareTables, snap: LumaSnapshot, side: 0 | 1, i: number, j: number): number {
  const k = side === 0 ? t.ka : t.kb
  const colOff = t.offsets[side * 2]
  const rowOff = t.offsets[side * 2 + 1]
  let sum = f(0)
  let n = 0
  for (let sj = 0; sj < k; sj++) {
    const y = t.index[rowOff + j * k + sj]
    if (y < 0) continue
    for (let si = 0; si < k; si++) {
      const x = t.index[colOff + i * k + si]
      if (x < 0) continue
      const code = snap.data[y * snap.width + x]
      if (t.values[(side * 256 + code) * 2 + 1] < 0.5) continue
      sum = f(sum + t.values[(side * 256 + code) * 2])
      n++
    }
  }
  return n === 0 || n * 2 < k * k ? NaN : f(sum / n)
}

/**
 * What the shader paints for cell (i, j): a palette entry, grey, or nothing
 * (null). The same steps in the same order, rounded to float32 at each.
 */
export function mirrorCell(t: LiveCompareTables, a: LumaSnapshot, b: LumaSnapshot, i: number, j: number): number | 'grey' | null {
  const va = mirrorMean(t, a, 0, i, j)
  const raw = mirrorMean(t, b, 1, i, j)
  const vb = Number.isNaN(raw) ? NaN : f(f(f(t.convert[0]) * raw) + f(t.convert[1]))
  const okA = !Number.isNaN(va)
  const okB = !Number.isNaN(vb)
  if (!okA && !okB) return null
  if (okA !== okB) return 'grey'
  const d = f(va - vb)
  let v = d
  if (t.mode === 1) {
    if (Math.abs(vb) < f(t.floor) || vb === 0) return 'grey'
    v = f(f(100 * d) / Math.abs(vb))
  }
  // AYNI: each side over its own end, as the shader does.
  const s = Math.max(-1, Math.min(1, f(v / f(v < 0 ? (t.lower ?? t.limit) : t.limit))))
  return Math.floor(f(f(f(s + 1) * 0.5) * 1023) + 0.5)
}

/**
 * The paused picture, painted by the shader's own arithmetic ({@link mirrorCell})
 * so that it and the live one show the same colour in every cell (the
 * statistics stay in float64, from `compareFields`). RGBA, row-major from
 * the north-west corner.
 */
export function paintLiveCompare(t: LiveCompareTables, a: LumaSnapshot, b: LumaSnapshot, alpha = 235): Uint8ClampedArray<ArrayBuffer> {
  const out = new Uint8ClampedArray(t.width * t.height * 4)
  const grey = t.grey.map((c) => Math.round(c * 255))
  for (let j = 0; j < t.height; j++) {
    for (let i = 0; i < t.width; i++) {
      const cell = mirrorCell(t, a, b, i, j)
      if (cell === null) continue
      const k = (j * t.width + i) * 4
      if (cell === 'grey') out.set([grey[0], grey[1], grey[2], alpha], k)
      else out.set([t.palette[cell * 4], t.palette[cell * 4 + 1], t.palette[cell * 4 + 2], alpha], k)
    }
  }
  return out
}
